import type { MethodKind } from './payments';

/**
 * All marketing copy for the landing page. Sections import from here; never inline strings.
 * Numbers the page states (markets, methods, coins) are not written here: `facts.ts` derives
 * them from the data, and a stat names which count it shows by `StatId`.
 */
/** One local coin of the XCoin network, as listed on the coins stage's orbit. */
export interface CoinItem {
  ticker: string;
  name: string;
  /** Currency symbol minted onto the coin face. */
  symbol: string;
  currency: string;
  /** The market's instant-payment rail, as the brief names it. */
  rail: string;
  /** Logo keys of that rail in the payment schedule, `<market>/<file>`; one plate each. */
  logos: string[];
}

export interface NavLink {
  /** Section element id; also the scroll target. */
  id: string;
  label: string;
}

/** A count `facts.ts` derives from the data; the copy only names which one a stat shows. */
export type StatId = 'markets' | 'methods' | 'coins';

export interface Stat {
  id: StatId;
  label: string;
}

/** Feature ids of the gateway section; the section maps each to an icon. */
export type GatewayFeatureId = 'orchestration' | 'dashboard' | 'controls' | 'api';

export interface GatewayFeature {
  id: GatewayFeatureId;
  title: string;
  /** What the capability is. */
  body: string;
  /** What the merchant gets from it, in one line. */
  outcome: string;
}

/** Metric ids of the dashboard mock; `activeMarkets` takes its value from the data. */
export type DashboardMetricId = 'processed' | 'successRate' | 'activeMarkets';

/** One line of the API section's request body, coloured by `kind`. */
export interface CodeField {
  key: string;
  value: string;
  kind: 'string' | 'number';
}

/** One step of what happens after a merchant gets in touch. */
export interface ContactStep {
  title: string;
  body: string;
}

export const copy = {
  brand: {
    name: 'XCoinGlobal',
    tagline: 'Local currencies. Global movement.',
  },
  /** Accessible names that are not visible text. */
  a11y: {
    backToTop: 'back to top',
    primaryNav: 'Primary',
    footerNav: 'Footer',
    menuOpen: 'Open menu',
    menuClose: 'Close menu',
  },
  nav: {
    links: [
      { id: 'coins', label: 'Coins' },
      { id: 'gateway', label: 'Gateway' },
      { id: 'coverage', label: 'Coverage' },
      { id: 'api', label: 'API' },
    ] satisfies NavLink[],
    /** Off-page link to the API documentation (`DOCS_URL`). */
    docs: 'Docs',
    cta: 'Partner with us',
  },
  hero: {
    eyebrow: 'Local payment infrastructure for Asia',
    /** One entry per visual line, each revealed by its own line mask. */
    title: ['One connection.', 'Every market.'],
    lede: 'Collect and pay out in local currency across Asia through one platform. Local banks, wallets, real-time QR networks and six local coins, behind one contract and one integration.',
    primary: { label: 'Explore the gateway', id: 'gateway' },
    secondary: { label: 'View available coins', id: 'coins' },
    /** Real counts under the CTAs, so the promise is backed before the first scroll. */
    proof: [
      { id: 'markets', label: 'markets' },
      { id: 'methods', label: 'payment methods' },
      { id: 'coins', label: 'local coins' },
    ] satisfies Stat[],
    cue: 'Scroll',
  },
  coins: {
    eyebrow: 'XCoin network',
    title: ['Local value,', 'ready to move.'],
    lede: 'Six market-specific coins give your customers the currency and the rails they already use, while you run one integration and one settlement layer behind them all.',
    /** The coin stage: a coin turning through the six faces inside its orbit of local rails. */
    stage: {
      /** Names the orbit list of chips, the section's only listing of coins and rails. */
      label: 'Six local coins on their instant-payment rails',
      cue: 'Scroll to open',
    },
    /** The HUD strip under the lede; the values come from `facts.ts`. */
    stats: [
      { id: 'coins', label: 'Local coins' },
      { id: 'markets', label: 'Markets covered' },
      { id: 'methods', label: 'Payment methods' },
    ] satisfies Stat[],
    /**
     * `symbol` is minted onto the coin face; `rail` is the market's instant-payment network and
     * `logos` its logo keys in the payment schedule (`payments.ts`), shown on the orbit chip.
     */
    items: [
      {
        ticker: 'XMYR',
        name: 'XRinggit',
        symbol: 'RM',
        currency: 'Malaysian Ringgit',
        rail: 'DuitNow',
        logos: ['malaysia/dnqr'],
      },
      {
        ticker: 'XTHB',
        name: 'XBaht',
        symbol: '฿',
        currency: 'Thai Baht',
        rail: 'PromptPay',
        logos: ['thailand/ppy'],
      },
      {
        ticker: 'XVND',
        name: 'XDong',
        symbol: '₫',
        currency: 'Vietnamese Dong',
        rail: 'VietQR',
        logos: ['vietnam/vnqr'],
      },
      {
        ticker: 'XIDR',
        name: 'XRupiah',
        symbol: 'Rp',
        currency: 'Indonesian Rupiah',
        rail: 'QRIS',
        logos: ['indonesia/qris'],
      },
      {
        ticker: 'XPHP',
        name: 'XPESO',
        symbol: '₱',
        currency: 'Philippine Peso',
        rail: 'GCash / Maya',
        logos: ['philippines/gc', 'philippines/mya'],
      },
      {
        ticker: 'XINR',
        name: 'XRupee',
        symbol: '₹',
        currency: 'Indian Rupee',
        rail: 'UPI',
        logos: ['india/upi'],
      },
    ] satisfies CoinItem[],
  },
  gateway: {
    eyebrow: 'Complete payment operations',
    title: ['Deposits in.', 'Payouts out.', 'One command centre.'],
    lede: 'Connect local banks, wallets, real-time payment networks and XCoin settlement through one operational layer, and run every market from the same screen.',
    features: [
      {
        id: 'orchestration',
        title: 'Unified deposit and payout orchestration',
        body: 'Route collections and disbursements across local payment partners from one integration.',
        outcome: 'Add a market without adding a stack.',
      },
      {
        id: 'dashboard',
        title: 'Real-time merchant dashboard',
        body: 'Monitor transactions, balances, settlements, success rates and exceptions as they happen.',
        outcome: 'Know where every payment stands, in every market.',
      },
      {
        id: 'controls',
        title: 'Automated controls',
        body: 'Apply configurable limits, verification rules, transaction monitoring and role-based access.',
        outcome: 'Set the rules once and let them run everywhere.',
      },
      {
        id: 'api',
        title: 'Seamless API',
        body: 'Use a consistent API for payment creation, status updates, webhooks, reconciliation and payouts.',
        outcome: 'Build once. Ship to every rail.',
      },
    ] satisfies GatewayFeature[],
    /**
     * The dashboard mock. Metric values count up from zero when the section enters. The volume
     * and success-rate figures are illustrative and the panel says so; the market count is
     * the schedule's.
     */
    dashboard: {
      label: 'Merchant dashboard preview',
      title: 'Payments overview',
      sample: 'Sample data',
      live: 'Live',
      metrics: [
        {
          id: 'processed',
          label: 'Processed today',
          value: 5,
          prefix: '$',
          suffix: 'M+',
          decimals: 0,
          delta: '+18.4%',
        },
        {
          id: 'successRate',
          label: 'Success rate',
          value: 97.8,
          suffix: '%',
          decimals: 1,
          delta: '+1.2%',
        },
        { id: 'activeMarkets', label: 'Active markets', decimals: 0, delta: 'Multi-currency' },
      ],
      chartLabel: 'Volume, last 24 hours',
      transactions: [
        { method: 'FPX', kind: 'Deposit', currency: 'MYR', status: 'Completed' },
        { method: 'GCash', kind: 'Payout', currency: 'PHP', status: 'Completed' },
        { method: 'PromptPay', kind: 'Deposit', currency: 'THB', status: 'Completed' },
      ],
    },
  },
  coverage: {
    eyebrow: 'Regional payment coverage',
    title: ['Let customers', 'pay their way.'],
    lede: 'Bring the bank transfers, real-time networks, QR payments and wallets your customers already trust into one integration. Pick a market to see exactly what it takes.',
    /** The sitewide totals under the lede; the counts come from `facts.ts`. */
    summary: (markets: number, methods: number) =>
      `${markets} markets and ${methods} payment methods, on one contract and one integration.`,
    /** Segmented control: the direction the facts strip and panel heading speak to. */
    modes: {
      label: 'Payment direction',
      deposit: { tab: 'Deposits', row: 'Deposit methods', minimum: 'Minimum deposit' },
      payout: { tab: 'Payouts', row: 'Payout methods', minimum: 'Minimum payout' },
    },
    /** The tab strip of markets; the markets themselves live in `content/payments.ts`. */
    markets: { label: 'Markets' },
    /** Column headings of a market panel, by method kind. */
    groups: {
      bank: 'Bank transfer',
      wallet: 'E-wallets & instant transfer',
      crypto: 'USDT networks',
    } satisfies Record<MethodKind, string>,
    /** Short names of the kinds, joined in the facts strip. */
    kinds: {
      bank: 'Banks',
      wallet: 'Wallets',
      crypto: 'USDT',
    } satisfies Record<MethodKind, string>,
    /** The facts strip: what the selected market takes, and the minimum for the direction. */
    facts: {
      methods: 'Methods',
      rails: 'Rails',
    },
    note: 'Coverage and minimums follow the XQuote payment-method schedule. Local bank and wallet availability may vary by payment partner, merchant category and onboarding approval.',
  },
  api: {
    eyebrow: 'Built for integration',
    title: ['One API across', 'every rail.'],
    lede: 'Launch new markets without rebuilding your payment stack country by country. Create a payment, receive a signed webhook and reconcile the result through one interface, whichever rail carried it.',
    points: [
      'REST API',
      'Signed webhooks',
      'Idempotent requests',
      'Sandbox environment',
      'Real-time status',
    ],
    /** Link to the API documentation (`DOCS_URL`). */
    docs: 'Read the API documentation',
    code: {
      label: 'Example request',
      method: 'POST',
      path: '/v1/payments',
      status: '201 Created',
      fields: [
        { key: 'amount', value: '2500.00', kind: 'number' },
        { key: 'currency', value: 'PHP', kind: 'string' },
        { key: 'method', value: 'GCASH', kind: 'string' },
        { key: 'type', value: 'DEPOSIT', kind: 'string' },
        { key: 'reference', value: 'ORD-10482', kind: 'string' },
        { key: 'status', value: 'PENDING', kind: 'string' },
      ] satisfies CodeField[],
    },
  },
  contact: {
    eyebrow: 'A single regional platform',
    title: ['Connect local money', 'to global opportunity.'],
    lede: 'Bring deposits, payouts, reconciliation and local currency coin settlement together under XCoinGlobal, and open your next market on the integration you already have.',
    /** What happens next, in three steps; reads the same whether or not a contact link is set. */
    steps: {
      label: 'How onboarding works',
      items: [
        {
          title: 'Choose your markets',
          body: 'Which countries and currencies, and whether you collect, pay out or both.',
        },
        {
          title: 'Map the rails',
          body: 'Each market is matched to the banks, wallets and networks in the schedule, with its minimums confirmed.',
        },
        {
          title: 'Integrate once',
          body: 'One API, a sandbox and signed webhooks. Every market after the first is configuration.',
        },
      ] satisfies ContactStep[],
    },
    /** Opens the partner form; hidden only when VITE_CONTACT_URL makes this section the destination. */
    primary: 'Partner with us',
    secondary: 'Explore documentation',
  },
  footer: {
    rights: 'All rights reserved.',
    links: [
      { id: 'coins', label: 'Coins' },
      { id: 'coverage', label: 'Coverage' },
      { id: 'api', label: 'API' },
    ] satisfies NavLink[],
    /** Off-page link to the API documentation (`DOCS_URL`). */
    docs: 'API docs',
  },
} as const;
